// apps/client/src/pages/pipeline-tracker/tabs/DeadlineShiftSection.tsx
//
// Operations > Overview: "Deadlines pushed out". Estimated settlement dates and task due dates that
// moved later between consecutive weekly uploads, per broker and per assignee, with the deals and
// tasks pushed most often.
//
// Deal references, owner and assignee names, task group labels and dates only. The server never
// returns a client name, a deal name or a raw task name, so there is nothing here to redact.

import React, { useState } from 'react';
import { CalendarArrowUp, CalendarClock, ChevronDown, ChevronRight, Hourglass } from 'lucide-react';
import { SkeletonTable } from '../../../components/common/SkeletonLoader';
import { useDeadlineShiftReport } from '../../../hooks/pipelineTracker/useDeadlineShiftReport';
import {
  uploadsLabel,
  type DeadlineShiftBuilding,
  type DeadlineShiftResult,
  type DealPushPart,
  type TaskPushPart,
} from '../../../hooks/pipelineTracker/deadlineShiftContract';
import { Honesty, Pill, num } from './pipelineParts';
import { Chip, HousePanel } from './trackerKit';
import { prettyDate } from './operationsParts';
import { useSectionAccent } from './TrackerSection';

export const DEADLINE_SHIFT_TITLE = 'Deadlines pushed out';

/** The panel's accent outside a TrackerSection: orange, apart from the register's red and the watchlist's amber. */
const ACCENT = '#f97316';

const TH = 'px-2 pb-2 font-medium';
const TH_R = 'px-2 pb-2 text-right font-medium';
/** Numbers in the house figure style: tabular figures in the body font, as the other tabs set them. */
const TD_R = 'px-2 py-2 text-right tabular-nums text-gray-300';
const TD = 'px-2 py-2 text-gray-200';
const HEAD_ROW = 'text-left text-[0.6875rem] uppercase tracking-wider text-gray-400';
/** Zebra and the subtle brand hover the compliance register uses. Never a white or grey box. */
const ROW = 'border-t border-gray-700/50 odd:bg-white/[0.015] transition-colors hover:bg-blue-500/[0.06]';

/** A deal or task's push count: red at three or more, amber at two, plain at one. */
const PushChip: React.FC<{ pushes: number }> = ({ pushes }) => (
  <Chip tone={pushes >= 3 ? 'red' : pushes === 2 ? 'amber' : 'muted'} className="tabular-nums">{num(pushes)}</Chip>
);

/** A person's count of items pushed two or more times: red at three or more, amber otherwise. */
const RepeatChip: React.FC<{ n: number }> = ({ n }) =>
  (n > 0
    ? <Chip tone={n >= 3 ? 'red' : 'amber'} className="tabular-nums">{num(n)}</Chip>
    : <span className="text-gray-500">{num(n)}</span>);

const days = (n: number | null): string => (n === null ? '-' : `${num(n)}d`);

const Building: React.FC<{ part: DeadlineShiftBuilding; report: string }> = ({ part, report }) => (
  <div
    className="rounded-lg border border-dashed border-blue-500/30 bg-blue-600/[0.06] px-4 py-3 text-sm text-blue-100"
    data-testid="deadline-shift-building"
  >
    <b className="font-semibold text-blue-300">
      {num(part.snapshotCount)} of {num(part.snapshotsNeeded)} {report} uploads so far.
    </b>{' '}
    A push shows once two weekly {report} uploads can be compared.
  </div>
);

const Collapsible: React.FC<{ label: string; count: number; children: React.ReactNode }> = ({
  label,
  count,
  children,
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 border-t border-gray-700/60">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 rounded-md px-2 py-2.5 text-left text-sm text-gray-200 transition-colors hover:bg-blue-500/[0.06] hover:text-white"
      >
        {open ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
        {label}
        <span className="ml-auto">
          <Pill>{num(count)}</Pill>
        </span>
      </button>
      {open && <div className="overflow-x-auto pb-2">{children}</div>}
    </div>
  );
};

/** Rows a per-person table shows before "Show all". The report sends them most pushes first. */
export const TOP_PEOPLE = 10;

/**
 * A per-person table capped at its top TOP_PEOPLE rows, with a "Show all N" expander in the
 * Most-pushed style. Open, the full list scrolls inside a bounded box under a sticky header, so a
 * practice with fifty assignees does not stretch the panel and leave the other half empty.
 */
const CappedTable: React.FC<{
  head: React.ReactNode;
  rows: React.ReactElement[];
  noun: string;
  testId: string;
}> = ({ head, rows, noun, testId }) => {
  const [all, setAll] = useState(false);
  const capped = rows.length > TOP_PEOPLE;
  const shown = all || !capped ? rows : rows.slice(0, TOP_PEOPLE);
  return (
    <div data-testid={testId}>
      <div className={`overflow-x-auto ${all && capped ? 'max-h-[26rem] overflow-y-auto' : ''}`}>
        <table className="w-full text-sm">
          <thead className={all && capped ? 'sticky top-0 z-10 bg-gray-900' : undefined}>{head}</thead>
          <tbody>{shown}</tbody>
        </table>
      </div>
      {capped && (
        <div className="mt-3 border-t border-gray-700/60">
          <button
            type="button"
            onClick={() => setAll((v) => !v)}
            aria-expanded={all}
            className="flex w-full items-center gap-2 rounded-md px-2 py-2.5 text-left text-sm text-gray-200 transition-colors hover:bg-blue-500/[0.06] hover:text-white"
          >
            {all ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
            {all ? `Show top ${num(TOP_PEOPLE)} ${noun}` : `Show all ${num(rows.length)} ${noun}`}
            <span className="ml-auto">
              <Pill>{num(rows.length)}</Pill>
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

const PartHeading: React.FC<{ title: string; window?: DealPushPart['window'] }> = ({ title, window }) => {
  // The dot wears the panel's colour, which inside a TrackerSection is the section's.
  const accent = useSectionAccent() ?? ACCENT;
  return (
    <div className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-100">
        <span aria-hidden="true" className="h-2 w-2 rounded-full" style={{ background: accent }} />
        {title}
      </h4>
      {window && (
        <span className="text-xs text-gray-400">
          {prettyDate(window.firstAsAtDate)} to {prettyDate(window.asAtDate)}, {uploadsLabel(window)}
        </span>
      )}
    </div>
  );
};

const DealsPart: React.FC<{ part: DealPushPart }> = ({ part }) => (
  <div data-testid="deadline-shift-deals">
    <PartHeading title="Estimated settlement dates pushed later" window={part.window} />
    {part.brokers.length === 0 ? (
      <p className="text-sm text-gray-400">No estimated settlement date was pushed later in these uploads.</p>
    ) : (
      <CappedTable
        testId="deadline-shift-brokers"
        noun="brokers"
        head={(
          <tr className={HEAD_ROW}>
            <th className={TH}>Broker</th>
            <th className={TH_R}>Deals pushed</th>
            <th className={TH_R}>Pushes</th>
            <th className={TH_R}>Median days</th>
            <th className={TH_R}>Pushed 2+ times</th>
          </tr>
        )}
        rows={part.brokers.map((b) => (
          <tr key={b.adviserKey || b.adviserName} className={ROW}>
            <td className={TD}>{b.adviserName}</td>
            <td className={TD_R}>{num(b.dealsWithPush)}</td>
            <td className={TD_R}>{num(b.pushes)}</td>
            <td className={TD_R}>{days(b.medianDaysPushed)}</td>
            <td className={TD_R}><RepeatChip n={b.repeatDeals} /></td>
          </tr>
        ))}
      />
    )}
    {part.topDeals.length > 0 && (
      <Collapsible label="Most-pushed deals" count={part.topDeals.length}>
        <table className="w-full text-sm">
          <thead>
            <tr className={HEAD_ROW}>
              <th className={TH}>Deal reference</th>
              <th className={TH}>Broker</th>
              <th className={TH}>Stage</th>
              <th className={TH_R}>Pushes</th>
              <th className={TH_R}>Days pushed</th>
              <th className={TH_R}>First estimate</th>
              <th className={TH_R}>Latest estimate</th>
            </tr>
          </thead>
          <tbody>
            {part.topDeals.map((d) => (
              <tr key={d.dealRef} className={ROW}>
                <td className="px-2 py-2 font-mono text-xs text-gray-300">{d.dealRef}</td>
                <td className={TD}>{d.adviserName}</td>
                <td className="px-2 py-2 text-gray-300">{d.stage ?? 'Not mapped'}</td>
                <td className={TD_R}><PushChip pushes={d.pushes} /></td>
                <td className={TD_R}>{days(d.totalDaysPushed)}</td>
                <td className={TD_R}>{prettyDate(d.firstEstimate)}</td>
                <td className={TD_R}>{prettyDate(d.latestEstimate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {part.totals.dealsWithPush > part.topDeals.length && (
          <p className="mt-2 text-xs text-gray-400">
            Showing {part.topDeals.length} of {num(part.totals.dealsWithPush)} deals pushed.
          </p>
        )}
      </Collapsible>
    )}
  </div>
);

const TasksPart: React.FC<{ part: TaskPushPart }> = ({ part }) => (
  <div data-testid="deadline-shift-tasks">
    <PartHeading title="Task due dates pushed later" window={part.window} />
    {part.assignees.length === 0 ? (
      <p className="text-sm text-gray-400">No open task's due date was pushed later in these uploads.</p>
    ) : (
      <CappedTable
        testId="deadline-shift-assignees"
        noun="assignees"
        head={(
          <tr className={HEAD_ROW}>
            <th className={TH}>Assignee</th>
            <th className={TH_R}>Tasks pushed</th>
            <th className={TH_R}>Pushes</th>
            <th className={TH_R}>Median days</th>
            <th className={TH_R}>Pushed 2+ times</th>
          </tr>
        )}
        rows={part.assignees.map((a) => (
          <tr key={a.assigneeName} className={ROW}>
            <td className={TD}>{a.assigneeName}</td>
            <td className={TD_R}>{num(a.tasksWithPush)}</td>
            <td className={TD_R}>{num(a.pushes)}</td>
            <td className={TD_R}>{days(a.medianDaysPushed)}</td>
            <td className={TD_R}><RepeatChip n={a.repeatTasks} /></td>
          </tr>
        ))}
      />
    )}
    {part.topTasks.length > 0 && (
      <Collapsible label="Most-pushed tasks" count={part.topTasks.length}>
        <table className="w-full text-sm">
          <thead>
            <tr className={HEAD_ROW}>
              <th className={TH}>Task group</th>
              <th className={TH}>Deal reference</th>
              <th className={TH}>Assignee</th>
              <th className={TH_R}>Pushes</th>
              <th className={TH_R}>Days pushed</th>
            </tr>
          </thead>
          <tbody>
            {part.topTasks.map((t, i) => (
              <tr key={`${t.dealRef ?? '-'}-${t.taskName}-${i}`} className={ROW}>
                <td className="px-2 py-2">
                  <Pill>{t.taskName}</Pill>
                </td>
                <td className="px-2 py-2 font-mono text-xs text-gray-300">{t.dealRef ?? 'No deal reference'}</td>
                <td className={TD}>{t.assigneeName}</td>
                <td className={TD_R}><PushChip pushes={t.pushes} /></td>
                <td className={TD_R}>{days(t.totalDaysPushed)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {part.totals.tasksWithPush > part.topTasks.length && (
          <p className="mt-2 text-xs text-gray-400">
            Showing {part.topTasks.length} of {num(part.totals.tasksWithPush)} tasks pushed.
          </p>
        )}
      </Collapsible>
    )}
  </div>
);

const NOTE =
  'Pushes are read by comparing weekly uploads, attributed to the deal\'s owner or the task\'s assignee; a date moved and moved back within one week is not seen.';

export const DeadlineShiftPanel: React.FC<{
  result: DeadlineShiftResult | null;
  loading: boolean;
  error: string | null;
}> = ({ result, loading, error }) => {
  if (loading) {
    return (
      <HousePanel title={DEADLINE_SHIFT_TITLE} icon={CalendarArrowUp} color={ACCENT}>
        <SkeletonTable rows={5} columns={5} />
      </HousePanel>
    );
  }
  if (error || !result) {
    return (
      <HousePanel title={DEADLINE_SHIFT_TITLE} icon={CalendarArrowUp} color={ACCENT}>
        <p className="text-sm text-amber-300">{error ?? 'Deadlines pushed out could not be loaded.'}</p>
      </HousePanel>
    );
  }

  const bothBuilding = result.deals.status === 'building' && result.tasks.status === 'building';
  return (
    <HousePanel
      title={DEADLINE_SHIFT_TITLE}
      icon={CalendarArrowUp}
      color={ACCENT}
      badge={
        bothBuilding ? (
          <Pill tone="brand">
            <Hourglass className="h-3 w-3" aria-hidden="true" />
            Building
          </Pill>
        ) : (
          <Pill tone="good">
            <CalendarClock className="h-3 w-3" aria-hidden="true" />
            Live
          </Pill>
        )
      }
      blurb={NOTE}
    >
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {result.deals.status === 'ok' ? (
          <DealsPart part={result.deals} />
        ) : (
          <div>
            <PartHeading title="Estimated settlement dates pushed later" />
            <Building part={result.deals} report="Performance" />
          </div>
        )}
        {result.tasks.status === 'ok' ? (
          <TasksPart part={result.tasks} />
        ) : (
          <div>
            <PartHeading title="Task due dates pushed later" />
            <Building part={result.tasks} report="Tasks" />
          </div>
        )}
      </div>
      {!bothBuilding && (
        <div className="mt-4">
          <Honesty>
            A push is a date at least {num(result.minPushDays)} days later than in the previous
            upload, so a one-day shift from how the export renders dates is not counted. Only deals
            still in flight and open tasks are read; a settled or lost deal and a completed task are
            left out. Each report reads at most its latest {num(result.snapshotCap)} uploads.
          </Honesty>
        </div>
      )}
    </HousePanel>
  );
};

const DeadlineShiftSection: React.FC<{ companyId: string; adviserId?: string }> = ({ companyId, adviserId }) => {
  const { result, loading, error } = useDeadlineShiftReport(companyId, adviserId);
  return <DeadlineShiftPanel result={result} loading={loading} error={error} />;
};

export default DeadlineShiftSection;
